import { useEffect, useRef, useState } from "react";
import StandardButton from "../UI/buttons/StandardButton";
import labels from "../../utils/labels";
import constraintStyle from "../../styles/constraint.module.css";
import ConstraintRow from "./ConstraintRow";
import { Constraint } from "../../types/constraint";
type DropdownOption = {
  label: string;
  isCategoryConstraint: boolean;
};

const defaultOptions: DropdownOption[] = [
  { label: labels.addCategoryConstraints, isCategoryConstraint: true },
  { label: labels.addIndividualConstraints, isCategoryConstraint: false },
];

const AddConstraintToggle = ({
  employeeRef,
  existingConstraints,
  options = defaultOptions,
}: {
  employeeRef: string;
  existingConstraints: Constraint[];
  options?: DropdownOption[];
}) => {
  const [showAddConstraint, setShowAddConstraint] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCategoryConstraint, setIsCategoryConstraint] = useState<
    boolean | undefined
  >();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleOptionClick = (isCategoryConstraint: boolean) => {
    setIsCategoryConstraint(isCategoryConstraint);
    setShowDropdown(false);
    setShowAddConstraint(true);
  };

  const handleCancel = () => {
    setShowAddConstraint(false);
    setIsCategoryConstraint(undefined);
  };

  useEffect(() => {
    if (showDropdown) optionRefs.current[0]?.focus();
  }, [showDropdown]);

  const closeDropdownAndFocusTrigger = () => {
    setShowDropdown(false);
    triggerRef.current?.focus();
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent) => {
    const currentIndex = optionRefs.current.findIndex(
      (el) => el === document.activeElement,
    );
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = (currentIndex + 1 + options.length) % options.length;
      optionRefs.current[nextIndex]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const nextIndex = (currentIndex - 1 + options.length) % options.length;
      optionRefs.current[nextIndex]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeDropdownAndFocusTrigger();
    }
  };

  const handleDropdownBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setShowDropdown(false);
    }
  };

  // ConstraintRow renders its own dataTableRow grid, so it must not be nested inside another
  // one here — a grid inside a grid cell would collapse to a single column instead of aligning.
  if (showAddConstraint) {
    return (
      <ConstraintRow
        isCategoryConstraint={isCategoryConstraint!}
        employeeRef={employeeRef}
        existingConstraints={existingConstraints}
        onAddCancel={handleCancel}
      />
    );
  }

  return (
    <div
      className={`${constraintStyle.dataTableRow} ${constraintStyle.staticRow}`}
      style={{ paddingLeft: "var(--padding-2xl)" }}
    >
      <div style={{ position: "relative" }}>
        <StandardButton
          ref={triggerRef}
          variant="TERTIARY"
          onClick={() => setShowDropdown((prev) => !prev)}
          ariaLabel={labels.addConstraint}
          ariaHaspopup="menu"
          ariaExpanded={showDropdown}
        >
          {labels.addConstraint}
        </StandardButton>
        {showDropdown && (
          <div
            className={constraintStyle.constraintDropdown}
            role="menu"
            onKeyDown={handleDropdownKeyDown}
            onBlur={handleDropdownBlur}
            // Safari doesn't focus buttons on click, so the blur would close the menu before the click lands.
            onMouseDown={(event) => event.preventDefault()}
          >
            {options.map((option, index) => (
              <button
                key={option.label}
                ref={(el) => (optionRefs.current[index] = el)}
                role="menuitem"
                aria-label={option.label}
                onClick={() => handleOptionClick(option.isCategoryConstraint)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddConstraintToggle;
